import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  ButtonProps,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useRef, useState } from 'react'
import {
  Button,
  Divider,
  Fade,
  Icons,
  InformationModal,
  ItemImageArea,
  SkeletonText,
} from 'utoniq-ui'

import {
  CommentsConnectionFragment,
  ItemCollectContent,
  Maybe,
  Mission,
  MissionLabel as EnumMissionLabel,
  MissionType,
  QuizContent,
  TwitterFollowContent,
  TwitterRetweetContent,
  User,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  CommentList,
  CommentListProps,
  ItemAsset,
  MissionLabel,
  MultipleItemsMission,
  ReplyFormRef,
  ReplyFormValues,
} from '@/components/organisms'
import {
  AreaNameChip,
  AreaStatusChip,
  GpsClientInfo,
  GpsMapModal,
  RequestPermissionModal,
} from '@/components/organisms/gps'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { openSetting } from '@/core/native/app'
import { useIsInside } from '@/core/native/geolocation'
import { usePrivateAction } from '@/lib/privateAction'

import { RetweetForm, RetweetFormValues } from './RetweetForm'
import { AnswerFormValues, UserAnswerForm } from './UserAnswerForm'

export * from './fragments'

export type State = {
  mission: Maybe<Mission>
  submittingAnswer: boolean
  loading: boolean
  sheet?: number
  hasNextPage: boolean
  comments: Maybe<CommentsConnectionFragment>
  user: Maybe<Pick<User, 'id' | 'formattedAccountId' | 'displayName'>>
  isLinked?: boolean
  incorrect?: boolean
  openModal?: boolean
  isGpsMission: boolean
  disabledItem: boolean
  gpsClientInfo: GpsClientInfo
  replyToDisplayName?: string
}

type LocationInfo = {
  isInside: boolean
  permission?: State['gpsClientInfo']['permission']
  targetData: Mission['gpsData']
  currentData: State['gpsClientInfo']
}

type MissionInfoCellProps = {
  loading: State['loading']
  mission: State['mission']
}

type MissionRewardCellProps = {
  loading: State['loading']
  mission: State['mission']
  user: State['user']
}

type MissionQuizCellProps = {
  loading: boolean
  content: QuizContent
  isAnswered: boolean
  onSubmitAnswer: Action['onSubmitAnswer']
  isGpsMission: boolean
  locationInfo: LocationInfo
  noAnswer: boolean
  label?: EnumMissionLabel | null
}

type TwitterRetweetMissionCellProps = {
  content: TwitterRetweetContent
  isLinked: State['isLinked']
  onSubmitTweet: Action['onSubmitTweet']
}

type TwitterFollowMissionCellProps = {
  content: TwitterFollowContent
  isLinked: State['isLinked']
  onFollow: Action['onFollow']
}

type MultipleItemsMissionCellProps = {
  loading: boolean
  content: ItemCollectContent
  isGpsMission: boolean
  isAnswered: boolean
  locationInfo: LocationInfo
  onSubmitAnswerItem: Action['onSubmitAnswerItem']
}

type ButtonAreaCellProps = Partial<ButtonProps> & {
  loading?: boolean
  label: string
}

type MissionUnaccomplishedCellProps = {
  incorrect?: State['incorrect']
  openModal: State['openModal']
  onCloseModal: Action['onCloseModal']
  missionType?: MissionType
  sheet?: number
}

type MissionLoadingSkeletonProps = {}

export type Action = {
  onSubmitAnswer: (values: AnswerFormValues) => Promise<void>
  onSubmitTweet: (values: RetweetFormValues) => Promise<void>
  onSubmitAnswerItem: () => Promise<void>
  onFollow: () => Promise<void>
  onCloseModal: () => void
  onFetchMore: CommentListProps['onFetchMore']
  onReply: (values: ReplyFormValues) => Promise<void>
  onLikeOrUnlikeComment: CommentListProps['onLikeOrUnlikeComment']
  onDeleteComment: CommentListProps['onDeleteComment']
  onReportUser: CommentListProps['onReportUser']
  onBlockUser: CommentListProps['onBlockUser']
  onReplyToReply: CommentListProps['onReplyToReply']
  onRemoveReplyToReply: () => void
}

/**
 * ミッション詳細ページ
 * https://zpl.io/VK37r4W
 */
export const MissionDetailTemplate: Template<State, Action> = ({
  state,
  onSubmitAnswer,
  onSubmitTweet,
  onSubmitAnswerItem,
  onFollow,
  onCloseModal,
  onFetchMore,
  onReply,
  onLikeOrUnlikeComment,
  onDeleteComment,
  onReportUser,
  onBlockUser,
  onReplyToReply,
  onRemoveReplyToReply,
}) => {
  const [showReply, setShowReply] = useState<boolean>(false)
  const { radius, ...missionGpsLocation } = state?.mission?.gpsData || {}
  const { routerNames, generatePath, routerPages } = useRouter()
  const replyFormRef = useRef<ReplyFormRef>(null)

  const { isInside } = useIsInside({
    locations: [missionGpsLocation, state.gpsClientInfo.currentLocation],
    radius,
  })

  const locationInfo = {
    isInside,
    permission: state?.gpsClientInfo?.permission,
    targetData: state?.mission?.gpsData,
    currentData: state?.gpsClientInfo,
  }

  return (
    <ArtistThemeProvider artistId={state.mission?.artist?.id}>
      <BaseLayout
        navigation={
          showReply
            ? {
                type: 'replyForm',
                props: {
                  ref: replyFormRef,
                  onReply: onReply,
                  visibleImage: true,
                  userId: state.user?.id ?? '',
                  replyToDisplayName: state.replyToDisplayName,
                  onRemoveReplyToReply: onRemoveReplyToReply,
                },
              }
            : undefined
        }
        header={{
          back: true,
          defaultBackPage: state.mission?.artist?.id
            ? config.applicationType === 'standalone'
              ? generatePath(routerPages.GlobalEvents, {
                  tab: 'mission',
                })
              : generatePath(routerPages.ArtistMissions, {
                  artistId: state.mission?.artist?.id ?? '#',
                })
            : undefined,
          title: state.mission?.title ?? '',
        }}
      >
        <Head title={[routerNames.MissionDetail, state.mission?.title]} />
        <MissionInfoCell mission={state.mission} loading={state.loading} />
        <MissionRewardCell
          mission={state.mission}
          loading={state.loading}
          user={state.user}
        />
        {state.loading ? (
          <MissionLoadingSkeleton />
        ) : (
          <>
            {state.mission?.type === MissionType.Quiz && (
              <MissionQuizCell
                loading={state.submittingAnswer}
                isAnswered={state.mission.label !== EnumMissionLabel.New}
                label={state.mission.label}
                noAnswer={state.mission.noAnswer}
                onSubmitAnswer={onSubmitAnswer}
                content={state.mission.content}
                isGpsMission={state.isGpsMission}
                locationInfo={locationInfo}
              />
            )}
            {state.mission?.type === MissionType.TwitterRetweet && (
              <TwitterRetweetMissionCell
                content={state.mission.content}
                isLinked={state.isLinked}
                onSubmitTweet={onSubmitTweet}
              />
            )}
            {state.mission?.type === MissionType.TwitterRetweet && (
              <TwitterRetweetMissionCell
                content={state.mission.content}
                isLinked={state.isLinked}
                onSubmitTweet={onSubmitTweet}
              />
            )}
            {state.mission?.type === MissionType.TwitterFollow && (
              <TwitterFollowMissionCell
                content={state.mission.content}
                isLinked={state.isLinked}
                onFollow={onFollow}
              />
            )}
            {state.mission?.type === MissionType.ItemCollect && (
              <MultipleItemsMissionCell
                loading={state.submittingAnswer}
                content={state.mission.content as ItemCollectContent}
                isGpsMission={state.isGpsMission}
                locationInfo={locationInfo}
                isAnswered={state.mission.label !== EnumMissionLabel.New}
                onSubmitAnswerItem={onSubmitAnswerItem}
              />
            )}
          </>
        )}

        <CommentList
          type="missions"
          typeId={state.mission?.id}
          showReaction
          onToggleButtonComment={() => setShowReply(prevState => !prevState)}
          user={state.user}
          commentCount={state.mission?.commentCount}
          comments={state.comments}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          onDeleteComment={onDeleteComment}
          onReportUser={onReportUser}
          onBlockUser={onBlockUser}
          onLikeOrUnlikeComment={onLikeOrUnlikeComment}
          onReplyToReply={params => {
            setShowReply(true)
            onReplyToReply && onReplyToReply(params)
          }}
          onClickReply={() => replyFormRef.current?.focus()}
        />
        <MissionUnaccomplishedCell
          incorrect={state.incorrect}
          openModal={state.openModal}
          onCloseModal={onCloseModal}
          missionType={state.mission?.type}
          sheet={state.sheet}
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const MissionInfoCell: Cell<MissionInfoCellProps> = ({ mission, loading }) => {
  const { t } = useTranslation('missionDetail')

  if (loading) return <MissionInfoSkeleton />

  return (
    <Fade>
      <CellFrame sx={{ pt: 2, px: 5 }}>
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {mission?.description}
        </Typography>
        {mission && (
          <MissionLabel
            sx={{
              borderRadius: '100px',
              pr: '12px',
              pl: '12px',
              height: '20px',
            }}
            label={mission.label ?? null}
          />
        )}
        <Stack sx={{ pt: 4 }} direction="row" spacing={3}>
          <Stack spacing={3}>
            <Typography variant="overline" color="text.hint">
              {t('period')}
            </Typography>
            {!mission?.noAnswer && (
              <Typography variant="overline" color="text.hint">
                {t('category')}
              </Typography>
            )}
          </Stack>
          <Divider orientation="vertical" flexItem />
          <Stack spacing={2}>
            <Typography variant="overline">
              {t('until', { date: mission?.formattedExpiredAt })}
            </Typography>
            {!mission?.noAnswer && (
              <Chip label={t(`${mission?.type}`)} size="small" />
            )}
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={3} sx={{ pt: 5 }}>
          <Avatar
            src={mission?.artist?.avatar?.smallUri ?? ''}
            alt={mission?.artist?.displayName ?? ''}
            sx={{ width: 36, height: 36 }}
          />
          <Typography variant="caption">
            {mission?.artist?.displayName}
          </Typography>
        </Stack>
      </CellFrame>
    </Fade>
  )
}

const MissionRewardCell: Cell<MissionRewardCellProps> = ({
  mission,
  loading,
  user,
}) => {
  const { t } = useTranslation('missionDetail')

  if (loading) return <MissionRewardSkelton />

  const isRewardItem = !!mission?.reward?.item
  const isRewardGacha = !!mission?.reward?.gachaTicket

  return (
    <Fade>
      <CellFrame sx={{ mt: 4, px: 5 }}>
        <Typography color="text.primary" variant="h6">
          {t('missionReward')}
        </Typography>
        <Box sx={{ py: 2 }}>
          <Accordion
            sx={{
              px: 2,
              boxShadow: 'none',
              '&:last-of-type, &:first-of-type': {
                borderRadius: 2,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<Icons.DownArrow fontSize="medium" />}
            >
              <Stack
                direction="row"
                alignItems={'center'}
                spacing={4}
                sx={{ width: '100%' }}
              >
                {isRewardItem && (
                  <>
                    <ItemImageArea
                      size="xx-small"
                      src={
                        mission?.reward?.item?.item?.thumbnail.front
                          ?.smallUri ?? ''
                      }
                    />
                    <Typography variant="h6">
                      {mission?.reward?.item?.item?.title}
                    </Typography>
                  </>
                )}
                {isRewardGacha && (
                  <>
                    <Box
                      sx={{
                        '& > *': {
                          height: '48px',
                        },
                      }}
                    >
                      <ItemImageArea
                        size="x-small"
                        src={
                          mission?.reward?.gachaTicket?.gachaTicket?.thumbnails
                            ?.standard?.uri ?? ''
                        }
                      />
                    </Box>
                    <Typography variant="h6">{t('gachaTicket')}</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Chip
                      label={
                        <Typography variant="subtitle2">
                          x{mission?.reward?.gachaTicket?.count}
                        </Typography>
                      }
                      size="small"
                      sx={{
                        backgroundColor: 'gray.200',
                        borderRadius: '100px',
                        pr: '12px',
                        pl: '12px',
                        height: '20px',
                      }}
                    />
                    <Box sx={{ width: 4 }} />
                  </>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ mt: -4, mb: 4 }}>
                {isRewardGacha
                  ? mission?.reward.gachaTicket?.gachaTicket.description
                  : mission?.reward?.item?.item?.description}
              </Typography>
              {isRewardItem && (
                <Box sx={{ pb: 1 }}>
                  <ItemAsset
                    user={user}
                    item={mission?.reward?.item?.item as any}
                  />
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </CellFrame>
    </Fade>
  )
}

export const GpsMapCell: Cell<{ locationInfo: LocationInfo }> = ({
  locationInfo,
}) => {
  const { t } = useTranslation('gps')
  const [openMap, setOpenMap] = useState(false)
  const [openRequestPermission, setOpenRequestPermission] = useState(false)

  return (
    <>
      <Fade>
        <CellFrame sx={{ mt: 4, px: 5 }}>
          <Stack id="gps-info-cell" alignItems="center" sx={{ py: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ pt: 2, pb: 3 }}
            >
              {t('gpsDescription')}
            </Typography>
            <AreaNameChip
              areaName={locationInfo?.targetData?.approximateAddress}
              sx={{ mt: 1, mb: 5, height: 'auto' }}
            />
            {locationInfo?.permission ? (
              <>
                <AreaStatusChip isInside={locationInfo.isInside} />
                <Typography
                  variant="subtitle2"
                  onClick={() => setOpenMap(true)}
                  sx={{ color: 'info.main', mt: 1, mb: 5 }}
                >
                  {t('confirmMap')}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h6" align="center">
                  {t('notGpsPermission')}
                </Typography>
                <Button
                  sx={{ mt: 4, mb: 7 }}
                  endIcon={<Icons.Window fontSize="large" color="inherit" />}
                  onClick={() =>
                    openSetting({
                      webSetting: () => setOpenRequestPermission(true),
                    })
                  }
                >
                  {t('toSetting')}
                </Button>
              </>
            )}
          </Stack>
        </CellFrame>
      </Fade>

      <GpsMapModal
        targetData={locationInfo?.targetData}
        currentData={locationInfo?.currentData?.currentLocation}
        isInside={locationInfo?.isInside}
        open={openMap}
        onClose={() => setOpenMap(false)}
      />
      <RequestPermissionModal
        open={openRequestPermission}
        onClose={() => setOpenRequestPermission(false)}
      />
    </>
  )
}

export const MissionQuizCell: Cell<MissionQuizCellProps> = ({
  loading,
  isAnswered,
  onSubmitAnswer,
  content,
  locationInfo,
  isGpsMission,
  noAnswer,
  label,
}) => {
  const { t } = useTranslation('missionDetail')

  return (
    <>
      <Fade>
        <CellFrame sx={{ mt: 4, px: 5 }}>
          {isGpsMission && <GpsMapCell locationInfo={locationInfo} />}
          <UserAnswerForm
            isAnswered={isAnswered}
            onSubmitAnswer={onSubmitAnswer}
            content={content}
            noAnswer={noAnswer}
          />
        </CellFrame>
      </Fade>
      <ButtonAreaCell
        loading={loading}
        disabled={isAnswered || (isGpsMission && !locationInfo?.isInside)}
        label={t(
          label === EnumMissionLabel.Expired
            ? 'expired'
            : noAnswer
            ? 'acquire'
            : 'answer'
        )}
        form="user-answer-form"
        type="submit"
      />
    </>
  )
}

export const TwitterRetweetMissionCell: Cell<TwitterRetweetMissionCellProps> =
  ({ content, isLinked, onSubmitTweet }) => {
    const { t } = useTranslation('missionDetail')
    return (
      <>
        <Fade>
          <CellFrame sx={{ mt: 4, pb: 5, px: 5 }}>
            <Card
              sx={{
                bgcolor: 'grey.50',
                borderRadius: 2,
                width: '100%',
              }}
            >
              <CardHeader
                avatar={
                  <Avatar
                    sx={{ width: 36, height: 36 }}
                    src={content.twitterProfileImage?.smallUri ?? ''}
                  />
                }
                action={<Icons.Twitter fontSize="medium" />}
                title={content.twitterName}
                subheader={content.twitterUserName}
                sx={{ px: 5, pt: 5, pb: 0 }}
              />
              <CardContent sx={{ p: 0 }}>
                <RetweetForm onSubmitTweet={onSubmitTweet} />
              </CardContent>
              <CardMedia
                component="img"
                height="197"
                image={content.image?.smallUri ?? ''}
                sx={{ px: 5, pt: 5, pb: 4 }}
              />
              <Card
                sx={{
                  mx: 5,
                  borderWidth: 1,
                  bgcolor: 'grey.50',
                  borderColor: 'grey.100',
                  borderStyle: 'solid',
                  borderRadius: 2,
                  boxShadow: 'none',
                }}
              >
                <CardHeader
                  avatar={
                    <Avatar
                      sx={{ width: 36, height: 36 }}
                      src={content.twitterProfileImage?.smallUri ?? ''}
                    />
                  }
                  title={content.twitterName}
                  subheader={content.twitterUserName}
                />
                <CardContent sx={{ pt: 0, pb: 4, px: 4 }}>
                  <Typography variant="body2">{content.tweetText}</Typography>
                </CardContent>
              </Card>
              <CardActions sx={{ px: 5 }}>
                <Typography variant="caption" color="text.hint">
                  {content.tweetCreatedAt}
                </Typography>
              </CardActions>
            </Card>
          </CellFrame>
        </Fade>
        <ButtonAreaCell
          label={isLinked ? t('verification') : t('workWithTwitter')}
          form="retweet-form"
          type="submit"
        />
      </>
    )
  }

export const TwitterFollowMissionCell: Cell<TwitterFollowMissionCellProps> = ({
  content,
  isLinked,
  onFollow,
}) => {
  const { t } = useTranslation('missionDetail')
  return (
    <>
      <Fade>
        <CellFrame sx={{ mt: 4, pb: 5, px: 5 }}>
          <Card
            sx={{
              bgcolor: 'grey.50',
              borderRadius: 4,
              width: '100%',
            }}
          >
            <Stack direction="row" sx={{ px: 5, pt: 5 }}>
              <Stack flex={1} />
              <Stack flex={3} alignItems="center">
                <Avatar
                  src={content.twitterProfileImage?.smallUri ?? ''}
                  title={content.twitterName ?? ''}
                  sx={{ width: 64, height: 64 }}
                />
                <Typography variant="h5" sx={{ pt: 2 }}>
                  {content.twitterName}
                </Typography>
                <Typography variant="subtitle2" color="text.hint">
                  {content.twitterUserName}
                </Typography>
              </Stack>
              <Stack flex={1} direction="row" justifyContent="flex-end">
                <Icons.Twitter fontSize="medium" />
              </Stack>
            </Stack>
            <CardContent sx={{ p: 5 }}>
              <Typography variant="body2">
                {content.twitterDescription}
              </Typography>
            </CardContent>
          </Card>
        </CellFrame>
      </Fade>
      <ButtonAreaCell
        label={isLinked ? t('verification') : t('workWithTwitter')}
        onClick={onFollow}
        type="button"
      />
    </>
  )
}

export const MultipleItemsMissionCell: Cell<MultipleItemsMissionCellProps> = ({
  loading,
  content,
  locationInfo,
  isGpsMission,
  isAnswered,
  onSubmitAnswerItem,
}) => {
  const { t } = useTranslation('missionDetail')
  return (
    <Fade>
      <CellFrame sx={{ pt: 5, px: 5 }}>
        {isGpsMission && <GpsMapCell locationInfo={locationInfo} />}
        <Stack spacing={5}>
          {content.items?.map((item, index) => (
            <MultipleItemsMission
              key={item.item.id}
              item={item.item}
              ownedCount={item.ownedCount}
              sheet={item.requiredCount}
            />
          ))}
        </Stack>
        <Stack sx={{ py: 6 }} alignItems="center">
          <Button
            size="large"
            sx={{ width: 244 }}
            disabled={isAnswered || loading}
            loading={loading}
            onClick={onSubmitAnswerItem}
          >
            {t('toEarn')}
          </Button>
        </Stack>
      </CellFrame>
    </Fade>
  )
}

export const ButtonAreaCell: Cell<ButtonAreaCellProps> = ({
  label,
  ...props
}) => {
  const { handleLoginModal } = usePrivateAction()
  return (
    <CellFrame
      sx={{
        py: 6,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <Button
        size="large"
        sx={{ minWidth: 244 }}
        {...props}
        onClick={handleLoginModal(props?.onClick)}
      >
        {label}
      </Button>
    </CellFrame>
  )
}

export const MissionUnaccomplishedCell: Cell<MissionUnaccomplishedCellProps> =
  ({ incorrect, openModal = false, onCloseModal, missionType, sheet }) => {
    const { t } = useTranslation('missionDetail')

    if (missionType === MissionType.ItemCollect) {
      return (
        <InformationModal
          open={openModal}
          onClose={onCloseModal}
          title={t('noItemConfirmTitle')}
          content={t('noItemConfirmDesc', { number: sheet })}
          buttons={
            <Button size="large" sx={{ width: 212 }} onClick={onCloseModal}>
              {t('ok')}
            </Button>
          }
        />
      )
    }

    return (
      <Dialog
        open={openModal}
        onClose={onCloseModal}
        sx={{
          textAlign: 'center',
          borderRadius: 3,
        }}
        PaperProps={{
          sx: {
            width: incorrect ? 335 : 280,
            bgcolor: 'grey.100',
            backgroundImage: 'unset',
          },
        }}
      >
        <DialogTitle sx={{ pt: incorrect ? 5 : 7 }}>
          {incorrect ? t('inCorrectAnswer') : t('conditionError')}
        </DialogTitle>
        <DialogContent sx={{ pb: 7 }}>
          <Typography variant="body2">
            {incorrect ? t('tryAgain') : t('errorMessage')}
          </Typography>
          <Button
            onClick={onCloseModal}
            fullWidth
            sx={incorrect ? { mt: 6, width: 244 } : { mt: 7, width: 212 }}
            variant="outlined"
            size="large"
          >
            {t('close')}
          </Button>
        </DialogContent>
      </Dialog>
    )
  }

export const MissionLoadingSkeleton: Cell<MissionLoadingSkeletonProps> = () => {
  return (
    <>
      <Box sx={{ px: 5, mt: 5 }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={335}
          sx={{ bgcolor: 'grey.100' }}
        />
      </Box>
      <Stack
        sx={{
          py: 6,
        }}
        flex={1}
        alignItems="center"
        justifyContent="center"
      >
        <Skeleton
          variant="rectangular"
          width={244}
          height={44}
          sx={{
            py: 3,
            px: 4,
            color: 'text.disabled',
            borderRadius: '34px',
            bgcolor: 'grey.100',
          }}
        />
      </Stack>
    </>
  )
}

export const MissionInfoSkeleton: Cell = () => {
  return (
    <Box sx={{ px: 5, py: 2 }}>
      <SkeletonText variant="medium" numberOfLine={3} lastWidth="75%" />
      <SkeletonText
        sx={{ mt: '44px' }}
        variant="small"
        numberOfLine={1}
        lastWidth="75%"
      />
      <SkeletonText variant="small" width="50%" />
      <Stack
        sx={{ mb: 0, mt: 5 }}
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="center"
      >
        <Skeleton
          sx={{ width: 36, height: 36, bgcolor: 'grey.100' }}
          variant="circular"
        />
        <SkeletonText
          sx={{ ml: 3 }}
          variant="small"
          numberOfLine={1}
          lastWidth="25%"
        />
      </Stack>
    </Box>
  )
}

// @todo check design
const MissionRewardSkelton: Cell = () => {
  return (
    <Box sx={{ px: 5, py: 2 }}>
      <SkeletonText variant="medium" numberOfLine={1} lastWidth="75%" />
      <Skeleton sx={{ width: 36, height: 36, bgcolor: 'grey.100' }} />
    </Box>
  )
}
